import React, { useState, useEffect } from "react";
import { Bookmark, ExternalLink, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import { getList, setList, logApplication } from "@/lib/storage";
import { companyLogo, companyInitials } from "@/lib/companyLogo";

function useStored(kind) {
  const [items, setItems] = useState(getList(kind));
  useEffect(() => {
    const handler = (e) => {
      if (!e.detail || e.detail.key?.includes(kind)) setItems(getList(kind));
    };
    window.addEventListener("jh:storage", handler);
    return () => window.removeEventListener("jh:storage", handler);
  }, [kind]);
  return [items, setItems];
}

function Row({ job, onRemove, onApply, action = "Apply" }) {
  const url = companyLogo(job.company);
  return (
    <div className="grid grid-cols-12 gap-3 items-center px-4 md:px-5 py-3 border-b-2 border-[#0a0a0a]/10" data-testid="saved-row">
      <div className="col-span-12 md:col-span-6 flex items-center gap-3 min-w-0">
        <div className="w-10 h-10 bg-[#FDFBF7] border-2 border-[#0a0a0a] flex items-center justify-center overflow-hidden">
          {url ? (
            <img src={url} alt={job.company} loading="lazy" className="w-full h-full object-contain" onError={(e) => (e.target.style.display = "none")} />
          ) : (
            <span className="font-display text-[10px]">{companyInitials(job.company)}</span>
          )}
        </div>
        <div className="min-w-0">
          <div className="font-display text-[14px] leading-tight truncate">{job.title}</div>
          <div className="font-mono-body text-[11px] text-[#0a0a0a]/65">{job.company} · {job.location || "—"}</div>
        </div>
      </div>
      <div className="col-span-6 md:col-span-3 font-mono-body text-[11px] text-[#0a0a0a]/65">
        {job.savedAt ? new Date(job.savedAt).toLocaleDateString() : job.alertAt ? new Date(job.alertAt).toLocaleDateString() : ""}
      </div>
      <div className="col-span-6 md:col-span-3 flex items-center justify-end gap-1.5">
        <button
          onClick={onRemove}
          aria-label="Remove"
          className="hover-pop w-9 h-9 border-2 border-[#0a0a0a] bg-white inline-flex items-center justify-center shadow-brutal-sm"
        >
          <Trash2 size={13} strokeWidth={3} />
        </button>
        <button
          onClick={onApply}
          className="hover-pop bg-[#E10600] text-white border-2 border-[#0a0a0a] px-3 py-2 font-accent text-[11px] shadow-brutal-sm inline-flex items-center gap-1.5"
        >
          {action} <ExternalLink size={11} strokeWidth={3} />
        </button>
      </div>
    </div>
  );
}

export default function DashboardSaved() {
  const [list] = useStored("saved");

  const remove = (url) => {
    setList("saved", getList("saved").filter((j) => j.url !== url));
  };
  const apply = (j) => {
    logApplication(j, "applied");
    window.open(j.url, "_blank", "noopener,noreferrer");
  };

  return (
    <div data-testid="dash-saved" className="space-y-5">
      <div>
        <h1 className="font-display text-3xl md:text-4xl flex items-center gap-3">
          <Bookmark className="text-[#E10600]" /> SAVED JOBS
        </h1>
        <p className="font-mono-body text-sm text-[#0a0a0a]/65 mt-1.5 max-w-xl">
          Your starred roles — synced across devices on this browser. Pick them up later, apply, or move them to Applications.
        </p>
      </div>

      <div className="bg-white border-4 border-[#0a0a0a] shadow-brutal">
        <div className="grid grid-cols-12 gap-3 px-4 md:px-5 py-3 bg-[#0a0a0a] text-[#FFC400] font-accent text-[10px]">
          <div className="col-span-12 md:col-span-6">ROLE</div>
          <div className="col-span-6 md:col-span-3">SAVED</div>
          <div className="col-span-6 md:col-span-3 text-right">ACTIONS</div>
        </div>
        {list.length === 0 ? (
          <div className="px-6 py-10 text-center font-mono-body text-sm text-[#0a0a0a]/65">
            Nothing saved yet — head to the <Link to="/dashboard/jobs" className="underline">Job Directory</Link> and bookmark a role.
          </div>
        ) : (
          list.map((j) => (
            <Row key={j.url} job={j} onRemove={() => remove(j.url)} onApply={() => apply(j)} action="Apply" />
          ))
        )}
      </div>
    </div>
  );
}
